/**
 * Offline cart persistence (Location Service Engine ``offline_cart``). The last cart the server priced is kept on
 * the device so the cart screen still shows it with no connection (and after an app restart), clearly marked as
 * offline. It is never used to place an order or to price anything — the server stays the only source of totals.
 *
 * Isolation: one entry per (tenant slug, customer id); the snapshot also records its shop. Entries expire after
 * 7 days and are dropped on sign-out (``clearCartSnapshots``). Storage failures are ignored.
 */
const PREFIX = "bizil.lse.cart.v1";
const TTL_MS = 7 * 24 * 3600 * 1000;

export const snapshotKey = (slug, userId) => `${PREFIX}:${slug || "-"}:${userId || "-"}`;

export function saveCartSnapshot(slug, userId, cart) {
    if (!userId || !cart) return;
    try {
        const body = { saved_at: Date.now(), restaurant_id: cart.restaurant_id || null, cart };
        localStorage.setItem(snapshotKey(slug, userId), JSON.stringify(body));
    } catch { /* storage full or blocked — offline view just isn't available */ }
}

export function loadCartSnapshot(slug, userId) {
    if (!userId) return null;
    try {
        const raw = localStorage.getItem(snapshotKey(slug, userId));
        if (!raw) return null;
        const body = JSON.parse(raw);
        if (!body?.cart || Date.now() - Number(body.saved_at || 0) > TTL_MS) return null;
        return { ...body.cart, _offline_saved_at: body.saved_at };
    } catch { return null; }
}

export function clearCartSnapshots(slug) {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && k.startsWith(`${PREFIX}:${slug ? `${slug}:` : ""}`)) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
}

/** A request that failed because there was no connection (not a server answer). */
export function isNetworkError(e) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
    return !!e && !e.response && (e.code === "ERR_NETWORK" || /network/i.test(String(e.message || "")));
}
